import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useCitationStyles } from "../../hooks/useCitation";
import { useCitationData } from "../../hooks/useCitation";
import { TextField, Stack, MenuItem } from "@mui/material";
import useCitationItemSwitch from "../../hooks/useCitation/use-citation-item-switch.hook";
import useCitationGenerate from "../../hooks/useCitation/use-citation-generate.hook";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { getRegularDate } from "utils/time.util";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import _ from "lodash";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { logEvent } from "services/amplitude.service";
import CloseIcon from "@mui/icons-material/Close";

import { itemTypes as localItemTypes } from "data/item-types";

const NOODLETOOLS_ACCESS_KEY = process.env.REACT_APP_NOODLETOOLS_ACCESS_KEY;
const NOODLETOOLS_CLIENT_KEY = process.env.REACT_APP_NOODLETOOLS_CLIENT_KEY;

const Citation = ({
  citation,
  setCitation,
  articleData,
  setArticleData,
  selectedStyle,
  setSelectedStyle,
  authors,
  setAuthors,
  selectedBookmarkOption,
  citationLoaded,
  setCitationLoaded,
  setOriginalArticleData,
  originalArticleData,
  setDisable,
  originalAuthors,
  setOriginalAuthors,
  disable,
}) => {
  const [currentItemType, setCurrentItemType] = useState("webpage");
  const [loading, setLoading] = useState(true);
  const [additionalData, setAdditionalData] = useState({});
  const [editingValue, setEditingValue] = useState("");
  const [editingField, setEditingField] = useState(null);
  const [date, setDate] = useState(null);
  const [accessDate, setAccessDate] = useState(null);
  const [authorIndex, setAuthorIndex] = useState(0);
  const [styleChanged, setStyleChanged] = useState(false);

  const formRef = React.useRef();
  const refWorksFormRef = React.useRef();

  const { itemTypes, summaryInfoData } = useSelector((state) => state.data);

  const { citationStyles } = useCitationStyles(
    selectedStyle,
    setSelectedStyle,
    selectedBookmarkOption,
    citationLoaded
  );
  const {
    baseFields,
    fields,
    requiredFields,
    setRequiredFields,
    setFields,
    setBaseFields,
    resetArticleData,
    resetAdditionalData,
  } = useCitationData(
    setLoading,
    setArticleData,
    setCurrentItemType,
    setAuthors,
    setDate,
    setAccessDate,
    setCitation,
    setSelectedStyle,
    articleData,
    additionalData,
    setAdditionalData,
    selectedBookmarkOption,
    citationLoaded,
    setCitationLoaded,
    setOriginalArticleData,
    setOriginalAuthors
  );

  const { handleItemChange } = useCitationItemSwitch(
    setLoading,
    baseFields,
    setBaseFields,
    additionalData,
    setAdditionalData,
    articleData,
    setArticleData,
    fields,
    setFields,
    setCurrentItemType,
    setRequiredFields,
    resetArticleData,
    resetAdditionalData
  );

  const { fetchCitation, tooltipText, handleCopyClick, handleMouseLeave } =
    useCitationGenerate(
      requiredFields,
      articleData,
      setLoading,
      citation,
      setCitation,
      currentItemType,
      selectedStyle,
      authors,
      setDisable,
      selectedBookmarkOption,
    );

  const buildRISData = () => {
    const lines = [];
    const risTypeMap = {
      blogPost: "BLOG",
      webpage: "ELEC",
      artwork: "ART",
      attachment: "GEN",
      audioRecording: "SOUND",
      bill: "BILL",
      book: "BOOK",
      bookSection: "CHAP",
      case: "CASE",
      computerProgram: "COMP",
      conferencePaper: "CPAPER",
      dataset: "DATA",
      dictionaryEntry: "DICT",
      document: "GEN",
      email: "PCOMM",
      encyclopediaArticle: "ENCYC",
      film: "MPCT",
      forumPost: "ICOMM",
      hearing: "HEAR",
      instantMessage: "ICOMM",
      interview: "INTV",
      journalArticle: "JOUR",
      letter: "PAMP",
      magazineArticle: "MGZN",
      manuscript: "MANSCPT",
      map: "MAP",
      newspaperArticle: "NEWS",
      patent: "PAT",
      podcast: "POD",
      preprint: "UNPB",
      presentation: "GEN",
      radioBroadcast: "SOUND",
      report: "RPRT",
      standard: "STAND",
      statute: "STAT",
      thesis: "THES",
      tvBroadcast: "MPCT",
      videoRecording: "VIDEO",
    };

    const currentItem = localItemTypes.find((item) => item.key.toLowerCase() === currentItemType.toLowerCase());
    const noodleToolsType = currentItem?.noodleTools;

    lines.push(`TY  - ${risTypeMap[currentItemType.toLowerCase()] || risTypeMap[currentItemType] || "GEN"}`);

    if (noodleToolsType) {
      lines.push(`M3  - ${noodleToolsType}`);
    }

    // Title mapping (handle Case Name for cases)
    const title = articleData.title || articleData.caseName || articleData.firstName;
    if (title) lines.push(`TI  - ${title}`);

    // Author mapping
    if (authors?.length > 0) {
      authors.forEach((author) => {
        if (author.firstName || author.lastName) {
          lines.push(`AU  - ${author.lastName}, ${author.firstName}`);
        }
      });
    }

    // 4. Date mapping (Strictly using citation.js date fields)
    const rawDate = articleData.date || articleData.dateDecided || articleData.issueDate || articleData.filingDate || articleData.dateEnacted;
    if (rawDate) {
      const d = new Date(rawDate);
      if (!isNaN(d.getTime()) && d.getFullYear() > 1970) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        lines.push(`PY  - ${year}`);
        lines.push(`DA  - ${year}/${month}/${day}`);
      }
    }

    // 5. Container Mapping (Ultra-Robust Source Name Detector)
    // This logic ensures that even if a user clears the UI fields, we recover the source name from original state.
    const findBestSource = () => {
      // Priority 1: User-edited fields
      const edited = articleData.publicationTitle || articleData.websiteTitle || articleData.blogTitle ||
        articleData.forumTitle || articleData.dictionaryTitle || articleData.encyclopediaTitle ||
        articleData.proceedingsTitle || articleData.programTitle;
      if (edited) return edited;

      // Priority 2: Original fetched metadata (immune to current UI clearing)
      const original = originalArticleData?.publicationTitle || originalArticleData?.websiteTitle ||
        originalArticleData?.blogTitle || originalArticleData?.journal ||
        originalArticleData?.source || originalArticleData?.name;
      if (original) return original;

      return "";
    };

    const effectiveContainerTitle = findBestSource();

    if (effectiveContainerTitle) {
      lines.push(`T2  - ${effectiveContainerTitle}`);
      const typeLower = currentItemType.toLowerCase();

      // Type-specific secondary mappings per user request
      if (typeLower === 'journalarticle') {
        lines.push(`JO  - ${effectiveContainerTitle}`);
      } else if (['magazinearticle', 'newspaperarticle'].includes(typeLower)) {
        lines.push(`JO  - ${effectiveContainerTitle}`);
        lines.push(`JF  - ${effectiveContainerTitle}`);
      } else if (typeLower === 'blogpost') {
        lines.push(`BT  - ${effectiveContainerTitle}`); // Custom tag for Blog Title per request
      }
    }

    // Series info
    if (articleData.series) lines.push(`T3  - ${articleData.series}`);
    if (articleData.seriesNumber) lines.push(`M1  - Series No. ${articleData.seriesNumber}`);

    // 6. Volume/Issue/Pages
    if (articleData.volume) lines.push(`VL  - ${articleData.volume}`);
    if (articleData.reporterVolume) lines.push(`VL  - ${articleData.reporterVolume}`);
    if (articleData.issue) lines.push(`IS  - ${articleData.issue}`);
    if (articleData.pages) lines.push(`SP  - ${articleData.pages}`);
    if (articleData.firstPage) lines.push(`SP  - ${articleData.firstPage}`);
    if (articleData.numPages && !articleData.pages) lines.push(`SP  - ${articleData.numPages}`);
    if (articleData.edition) lines.push(`ET  - ${articleData.edition}`);

    // 7. Legal/Governmental/Technical Specifics
    if (articleData.court) lines.push(`CT  - ${articleData.court}`);
    if (articleData.docketNumber) lines.push(`ID  - ${articleData.docketNumber}`);
    if (articleData.reporter) lines.push(`JO  - ${articleData.reporter}`);
    if (articleData.legislativeBody) lines.push(`PB  - ${articleData.legislativeBody}`);
    if (articleData.session) lines.push(`M1  - Session: ${articleData.session}`);
    if (articleData.billNumber) lines.push(`M1  - Bill: ${articleData.billNumber}`);
    if (articleData.publicLawNumber) lines.push(`M1  - Public Law: ${articleData.publicLawNumber}`);
    if (articleData.patentNumber) lines.push(`PN  - ${articleData.patentNumber}`);
    if (articleData.assignee) lines.push(`PB  - ${articleData.assignee}`);

    // 8. Work Medium and Metadata hints from citation.js
    const mediumHint =
      articleData.artworkMedium ||
      articleData.format ||
      articleData.audioFileType ||
      articleData.videoRecordingFormat ||
      articleData.postType ||
      articleData.thesisType ||
      articleData.reportType ||
      articleData.presentationType ||
      articleData.genre;

    if (mediumHint) {
      if (noodleToolsType) {
        lines.push(`M1  - ${mediumHint}`);
      } else {
        lines.push(`M3  - ${mediumHint}`);
      }
    }
    if (articleData.runningTime) lines.push(`M1  - Run Time: ${articleData.runningTime}`);
    if (articleData.episodeNumber) lines.push(`M1  - Episode: ${articleData.episodeNumber}`);
    if (articleData.network) lines.push(`M1  - Network: ${articleData.network}`);

    // 9. URL and Access Date
    if (articleData.url) lines.push(`UR  - ${articleData.url}`);
    if (articleData.accessDate) {
      const ad = new Date(articleData.accessDate);
      if (!isNaN(ad.getTime())) {
        const year = ad.getFullYear();
        const month = String(ad.getMonth() + 1).padStart(2, '0');
        const day = String(ad.getDate()).padStart(2, '0');
        lines.push(`Y2  - ${year}/${month}/${day}`);
      }
    }

    // 10. Secondary Metadata (mapped strictly from citation.js)
    if (articleData.abstractNote) lines.push(`AB  - ${articleData.abstractNote}`);
    if (articleData.language) lines.push(`LA  - ${articleData.language}`);

    // Publisher mapping with fallback for Books, Webpages, etc.
    const publisher =
      articleData.publisher ||
      articleData.university ||
      articleData.institution ||
      articleData.company ||
      articleData.organization ||
      (['book', 'webpage', 'booksection'].includes(currentItemType.toLowerCase()) ? effectiveContainerTitle : null);

    if (publisher) {
      lines.push(`PB  - ${publisher}`);
    }

    if (articleData.place) lines.push(`CY  - ${articleData.place}`);
    if (articleData.ISBN) lines.push(`SN  - ${articleData.ISBN}`);
    if (articleData.ISSN) lines.push(`SN  - ${articleData.ISSN}`);
    if (articleData.DOI) lines.push(`DO  - ${articleData.DOI}`);
    if (articleData.shortTitle) lines.push(`ST  - ${articleData.shortTitle}`);
    if (articleData.extra) lines.push(`N1  - ${articleData.extra}`);
    if (articleData.notes) lines.push(`N1  - ${articleData.notes}`);
    if (articleData.archive) lines.push(`M1  - Archive: ${articleData.archive}`);
    if (articleData.archiveLocation) lines.push(`M1  - Location: ${articleData.archiveLocation}`);
    if (articleData.repository) lines.push(`DB  - ${articleData.repository}`);
    if (articleData.libraryCatalog) lines.push(`DP  - ${articleData.libraryCatalog}`);

    lines.push("ER  - ");
    return lines.join("\n");
  };

  const handleExportToNoodleTools = () => {
    const risString = buildRISData();
    // Set the textarea value directly on the DOM element before submitting
    if (formRef.current) {
      const textarea = formRef.current.querySelector(
        "textarea[name='risdatabyformpost']"
      );
      if (textarea) textarea.value = risString;
      formRef.current.submit();
    }
  };

  const handleExportToRefWorks = () => {
    const risString = buildRISData();
    if (refWorksFormRef.current) {
      const textarea = refWorksFormRef.current.querySelector(
        "textarea[name='ImportData']"
      );
      if (textarea) textarea.value = risString;
      refWorksFormRef.current.submit();
    }
  };

  const downloadFile = (data, filename, type) => {
    const blob = new Blob([data], { type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadRIS = () => {
    downloadFile(
      buildRISData(),
      "citation.ris",
      "application/x-research-info-systems"
    );
  };

  const buildBibTeXData = () => {
    const entryType = currentItemType === "book" ? "book" : "misc";
    let bib = `@${entryType}{citation,\n`;
    if (articleData.title) bib += `  title = {${articleData.title}},\n`;
    if (authors?.length > 0) {
      const authorList = authors
        .filter((a) => a.firstName || a.lastName)
        .map((a) => `${a.lastName || ""}, ${a.firstName || ""}`)
        .join(" and ");
      if (authorList) bib += `  author = {${authorList}},\n`;
    }
    if (articleData.date) {
      const d = new Date(articleData.date);
      if (!isNaN(d)) bib += `  year = {${d.getFullYear()}},\n`;
    }
    if (articleData.url) bib += `  url = {${articleData.url}},\n`;
    if (articleData.publication)
      bib += `  journal = {${articleData.publication}},\n`;
    bib += `}`;
    return bib;
  };

  const handleDownloadBibTeX = () => {
    downloadFile(buildBibTeXData(), "citation.bib", "application/x-bibtex");
  };



  const handleSelectStyleType = (e) => {
    if (e.target.value === selectedStyle) {
      setStyleChanged(false);
    } else {
      setStyleChanged(true);
    }
    setSelectedStyle(e.target.value);
  };

  const handleSelectItemType = (e) => {
    handleItemChange(e.target.value, 0);
  };

  const CitationBox = () => {
    const hasMissingFields = requiredFields.some((field) => {
      if (field === "creators") {
        return !authors || authors.length === 0 || !authors[0]?.firstName;
      }
      const value = articleData[field];
      if (value === null || value === undefined || value === "") {
        return true;
      }
      // Robust check for invalid or default (1970) dates
      if (field === "date" || field === "accessDate") {
        let d = null;
        if (value && typeof value === "object") {
          if ("isValid" in value && typeof value.isValid === "function") {
            if (!value.isValid()) return true;
            d = value.toDate();
          } else if (value instanceof Date) {
            if (isNaN(value.getTime())) return true;
            d = value;
          }
        } else if (typeof value === "string") {
          const tempDate = new Date(value);
          if (isNaN(tempDate.getTime())) return true;
          d = tempDate;
        }

        // Treat 1970 or earlier as missing/invalid for NoodleTools export
        if (d && d.getFullYear() <= 1970) {
          return true;
        }
      }
      return !value;
    });
    const shouldShowMissingState = hasMissingFields && !citation;

    if (!citation && !shouldShowMissingState) {
      return null;
    }

    return (
      <Box
        sx={{
          mb: 2,
          zIndex: 1000,
        }}
      >
        {citation ? (
          <>
            <Typography variant="body3" color="grey.600">
              Generated Citation
            </Typography>
            <Typography
              sx={{
                marginTop: "1",
                maxHeight: "15vh",
                overflowY: "auto",
                overflowX: "hidden",
                p: 1,
              }}
              variant="subtitle2"
              dangerouslySetInnerHTML={{ __html: citation }}
            />
            <Stack direction="row" gap={2} mt={1}>
              <Tooltip title={tooltipText} arrow>
                <Button
                  startIcon={<ContentCopyIcon />}
                  sx={{ px: 2, py: 1 }}
                  variant="outlined"
                  onClick={handleCopyClick}
                  onMouseLeave={handleMouseLeave}
                >
                  Copy
                </Button>
              </Tooltip>
              {NOODLETOOLS_CLIENT_KEY && (
                <form
                  ref={formRef}
                  action="https://my.noodletools.com/import/rest-post.php"
                  target="_blank"
                  method="POST"
                >
                  <input
                    type="hidden"
                    name="accesskey"
                    value={NOODLETOOLS_ACCESS_KEY}
                  />
                  <input
                    type="hidden"
                    name="clientkey"
                    value={NOODLETOOLS_CLIENT_KEY}
                  />
                  <input type="hidden" name="version" value="1.0" />
                  <input type="hidden" name="datatype" value="ris" />
                  <textarea
                    name="risdatabyformpost"
                    defaultValue=""
                    readOnly
                    style={{ display: "none" }}
                  />
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleExportToNoodleTools}
                    disabled={hasMissingFields}
                    sx={{
                      px: 2,
                      py: 1,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Export to NoodleTools
                  </Button>
                </form>
              )}
            </Stack>
            {NOODLETOOLS_CLIENT_KEY && (
              <Box mt={1} px={1}>
                {hasMissingFields ? (
                  <Typography variant="caption" color="error.main" sx={{ display: "block" }}>
                    Please fill in all required fields to export to Noodle Tools
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    Please ensure your citation is correct before you save, copy, or export it.
                  </Typography>
                )}
              </Box>
            )}
          </>
        ) : (
          <Typography
            variant="subtitle2"
            sx={{
              color: "error.main",
              marginTop: 1,
              p: 1,
            }}
          >
            Please scroll down to populate the missing information.
          </Typography>
        )}
      </Box>
    );
  };

  const renderFieldValue = (field) => {
    if (editingField === field) {
      return editingValue;
    } else
      switch (field) {
        case "date":
          return getRegularDate(articleData[field]);
        case "accessDate":
          return getRegularDate(articleData[field]);
        default:
          return articleData[field];
      }
  };

  const handleBlur = (field) => {
    setEditingField(null);
    if (field === "creators" || field === "author") {
    } else {
      setArticleData((prevData) => ({
        ...prevData,
        [field]: editingValue,
      }));
    }
  };

  const handleInputChange = (value) => {
    setEditingValue(value);
  };

  const addAuthor = (position) => {
    const newAuthor = {
      firstName: "",
      lastName: "",
      creatorType: "author",
    };

    setAuthors([
      ...authors.slice(0, position),
      newAuthor,
      ...authors.slice(position),
    ]);
  };

  const removeAuthor = (index) => {
    const newAuthors = [...authors];
    newAuthors.splice(index, 1);
    setAuthors(newAuthors);
    setArticleData((prevData) => ({ ...prevData, creators: newAuthors }));
  };

  const handleAuthorChange = (index, field, value) => {
    const newAuthors = [...authors];
    newAuthors[index][field] = value;
    setAuthors(newAuthors);
  };

  const handleDateChange = (field, val) => {
    if (field === "date") {
      setDate(val);
    } else {
      setAccessDate(val);
    }
    setArticleData((prevData) => ({ ...prevData, [field]: val }));
  };

  const handleAuthorBlur = () => { };

  const checkRequiredFields = (field) => {
    if (field === "creators") {
      return (
        requiredFields.includes(field) && !authors[0]?.firstName && !(editingField === "creators" && editingValue)
      );
    }
    if (field === "date" || field === "accessDate") {
      if (!requiredFields.includes(field)) return false;
      const val = articleData[field];
      if (!val) return true;
      if (typeof val === "object" && "isValid" in val) return !val.isValid();
      const d = new Date(val);
      return isNaN(d.getTime()) || d.getFullYear() <= 1970;
    }
    return (
      requiredFields.includes(field) && !articleData[field] && !(editingField === field && editingValue)
    );
  };

  const RenderEditableField = (field) => {
    let required = checkRequiredFields(field);
    return field === "creators" ? (
      <Box key={field} tabIndex={-1} onBlur={handleAuthorBlur}>
        {authors.map((author, index) => (
          <Box mt={2} mb={2} key={`${field}-${index}`}>
            <Stack
              direction="row"
              alignItems="start"
              justifyContent="space-between"
              gap={2}
            >
              <TextField
                value={
                  editingField === "firstName" && index === authorIndex
                    ? editingValue
                    : author.firstName
                }
                onFocus={() => {
                  setAuthorIndex(index);
                  setEditingField("firstName");
                  setEditingValue(author.firstName);
                }}
                onBlur={(e) =>
                  handleAuthorChange(index, "firstName", editingValue)
                }
                onChange={(e) => handleInputChange(e.target.value)}
                label="First Name"
                sx={{
                  borderColor: required ? "red" : "",
                }}
                error={required}
                helperText={required ? "This field is required" : ""}
              />
              <TextField
                value={
                  editingField === "lastName" && index === authorIndex
                    ? editingValue
                    : author.lastName
                }
                onFocus={() => {
                  setAuthorIndex(index);
                  setEditingField("lastName");
                  setEditingValue(author.lastName);
                }}
                onBlur={(e) =>
                  handleAuthorChange(index, "lastName", editingValue)
                }
                onChange={(e) => handleInputChange(e.target.value)}
                label="Last Name"
              />
              <AddIcon
                onClick={() => addAuthor(index + 1)}
                sx={{ cursor: "pointer", mt: 1 }}
              />
              <DeleteIcon
                onClick={() => {
                  if (index !== 0) {
                    removeAuthor(index);
                  }
                }}
                sx={{
                  cursor: index === 0 ? "default" : "pointer",
                  color: index === 0 ? "gray" : "inherit",
                  mt: 1,
                }}
              />
            </Stack>
          </Box>
        ))}
      </Box>
    ) : field === "date" || field === "accessDate" ? (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          sx={{ mb: 2 }}
          label={field === "date" ? "Date" : "Access Date"}
          value={field === "date" ? date : accessDate}
          onChange={(val) => handleDateChange(field, val)}
          slotProps={{
            textField: {
              error: checkRequiredFields(field),
              helperText: checkRequiredFields(field) ? "This field is required" : "",
            },
          }}
        />
      </LocalizationProvider>
    ) : (
      <TextField
        key={field}
        value={editingField === field ? editingValue : renderFieldValue(field)}
        onFocus={() => {
          setEditingField(field);
          setEditingValue(renderFieldValue(field));
        }}
        onBlur={() => handleBlur(field)}
        label={fields[field]}
        sx={{
          borderColor: required ? "red" : "",
          mb: 2,
        }}
        error={required}
        helperText={required ? "This field is required" : ""}
        onChange={(e) => handleInputChange(e.target.value)}
      />
    );
  };

  useEffect(() => {
    if (articleData) {
      const articleDataChanged =
        JSON.stringify(articleData) !== JSON.stringify(originalArticleData);
      const authorsChanged =
        JSON.stringify(authors) !== JSON.stringify(originalAuthors);
      const isDataModified = articleDataChanged || authorsChanged || styleChanged;

      setDisable(!isDataModified);
      fetchCitation(fields, isDataModified);
    }
  }, [selectedBookmarkOption, articleData, authors, selectedStyle]);

  if (selectedBookmarkOption !== "citation") {
    return <></>;
  }
  if (loading) {
    return (
      <Stack direction="column" gap={2} p={2}>
        <Skeleton variant="rectangular" height={118} />
        <Skeleton variant="rectangular" height={50} />
        <Skeleton variant="rectangular" height={50} />
        <Skeleton variant="rectangular" height={50} />
        <Skeleton variant="rectangular" height={50} />{" "}
      </Stack>
    );
  }

  return (
    <Stack gap={2} p={4}>
      <CitationBox />

      <TextField
        select
        label="Style Type"
        value={selectedStyle}
        onChange={handleSelectStyleType}
        fullWidth
        variant="outlined"
        sx={{ mb: 2 }}
      >
        {citationStyles?.map((style) => (
          <MenuItem key={style["key"]} value={style["key"]}>
            {style["name"]}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Item Type"
        value={currentItemType}
        onChange={handleSelectItemType}
        fullWidth
        variant="outlined"
        sx={{ mb: 2 }}
      >
        {itemTypes.map((style) => (
          <MenuItem key={style["key"]} value={style["key"]}>
            {style["name"]}
          </MenuItem>
        ))}
      </TextField>
      {Object.keys(fields).map((field) => RenderEditableField(field))}


    </Stack>
  );
};

export default Citation;
